import React from 'react';
import { usePlatformNavigation } from 'src/utils/navigation';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';
import { client } from 'src/utils/api-client';
import { useBranchesWithCountry } from 'src/utils/branches';
import { usePlatform } from 'src/redux-file/hooks';
import { OrganizationTag } from 'src/utils/tags';
import PageLoading from 'src/components/PageLoading';
import {
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  ListItemIcon,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

interface Props {
  open: boolean;
  onClose: () => void;
}

// The subset of `OrganizationDataOutListSchema` (backend/organizations/
// api_schemas.py) this dialog actually renders on. `branch_country_code(s)`
// is the additive C05 pair (D-06): a single code when the org's own
// branches sit in exactly one country, null otherwise, with
// `branch_country_codes` always carrying the full list so we can tell
// "no branches" apart from "several countries". `co2`/`esg`/`lca`/`cbam`
// mirror the pre-existing per-product fields (non-null only when that
// product is active for the org) -- used only to preserve today's
// `usePlatformNavigation().productOrganizations` filtering below. `tags`
// is what marks the Raben root, which is pinned above everything else.
interface OrgSwitchRow {
  id: number;
  name: string;
  tags?: string[] | null;
  branch_country_code: string | null;
  branch_country_codes: string[];
  co2: unknown | null;
  esg: unknown | null;
  lca: unknown | null;
  cbam: unknown | null;
}

interface OrgSwitchPage {
  items: OrgSwitchRow[];
  total: number;
  page: number;
  page_size: number;
}

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const rowSx = {
  p: '10px',
  borderTop: '1px solid #e0e0e0',
};

const groupHeaderSx = {
  px: '10px',
  py: '6px',
  fontSize: '13px',
  fontWeight: 700,
  color: 'text.secondary',
  backgroundColor: '#f5f5f5',
};

const isRabenRoot = (tags: string[] | null | undefined) =>
  !!tags?.includes(OrganizationTag.RabenRoot);

function OrganizationRow({
  organization,
  depth,
  selected,
  onSelect,
}: {
  organization: Pick<OrgSwitchRow, 'id' | 'name'>;
  depth: number;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <MenuItem
      sx={{ ...rowSx, pl: `${depth * 20}px` }}
      selected={selected}
      onClick={onSelect}
    >
      <ListItemIcon>
        <BusinessIcon />
      </ListItemIcon>
      <Typography sx={{ textWrap: 'wrap' }}>{organization.name}</Typography>
    </MenuItem>
  );
}

function GroupHeader({ label }: { label: string }) {
  return <Typography sx={groupHeaderSx}>{label}</Typography>;
}

export default function OrganizationsTreeModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const { organizationId, navigateToOrganization, activeProduct } =
    usePlatformNavigation();
  const { organizations } = usePlatform();
  const { branches, isLoading: isCountriesLoading } = useBranchesWithCountry();

  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');

  // Goal 1: debounce the search box 300ms before it hits the server --
  // one request per pause, not one per keystroke.
  React.useEffect(() => {
    const handle = setTimeout(
      () => setSearch(searchInput.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Every fresh open starts from a clean slate rather than carrying over
  // whatever was typed the last time the dialog was closed.
  React.useEffect(() => {
    if (open) {
      setSearchInput('');
      setSearch('');
    }
  }, [open]);

  const orgsQuery = useInfiniteQuery<OrgSwitchPage>(
    ['organizations-switch-dialog', search],
    ({ pageParam = 1 }) =>
      client
        .get<OrgSwitchPage>('/web/organizations', {
          params: {
            search: search || undefined,
            page: pageParam,
            page_size: PAGE_SIZE,
          },
        })
        .then((res) => res.data),
    {
      enabled: open,
      getNextPageParam: (lastPage) =>
        lastPage.page * lastPage.page_size < lastPage.total
          ? lastPage.page + 1
          : undefined,
    }
  );

  const countryNameByCode = React.useMemo(() => {
    const map: Record<string, string> = {};
    branches.forEach((branch) => {
      if (branch.country_code && !map[branch.country_code]) {
        map[branch.country_code] = branch.country_name || branch.country_code;
      }
    });
    return map;
  }, [branches]);

  const orgs = React.useMemo(
    () => orgsQuery.data?.pages.flatMap((page) => page.items) || [],
    [orgsQuery.data]
  );

  // Preserve today's behaviour (`usePlatformNavigation().productOrganizations`):
  // while browsing a specific product's own pages, the switch list only
  // offers orgs that actually have that product active, so switching never
  // lands on a dashboard the target org doesn't have.
  const visibleOrgs = React.useMemo(
    () =>
      activeProduct
        ? orgs.filter((org) => (org as any)[activeProduct] != null)
        : orgs,
    [orgs, activeProduct]
  );

  // Goal 2 / EC-2: bucket every row exactly once. The Raben root is pinned
  // first, above every country section, whatever its branches are: it has
  // some in several countries, which would otherwise drop it into the
  // "multiple countries" section at the very bottom. Of the rest, zero
  // branch countries (any unit with no branches yet) -> header-less section
  // right below the root. Exactly one -> that country's header. More than
  // one -> the shared "multiple countries" header, never duplicated across
  // the countries it spans.
  const { pinned, ungrouped, countrySections, multiCountry } =
    React.useMemo(() => {
      const pinnedRows = new Map<number, Pick<OrgSwitchRow, 'id' | 'name'>>();
      const ungroupedRows: OrgSwitchRow[] = [];
      const multiCountryRows: OrgSwitchRow[] = [];
      const byCountry = new Map<string, OrgSwitchRow[]>();

      const profileRoots = (organizations ?? []).filter((org) =>
        isRabenRoot(org.tags)
      );
      const rootIds = new Set(profileRoots.map((org) => org.id));

      visibleOrgs.forEach((org) => {
        if (rootIds.has(org.id) || isRabenRoot(org.tags)) {
          pinnedRows.set(org.id, org);
          return;
        }
        const codes = org.branch_country_codes;
        if (codes.length === 0) {
          ungroupedRows.push(org);
        } else if (codes.length > 1) {
          multiCountryRows.push(org);
        } else {
          const [code] = codes;
          if (!byCountry.has(code)) byCountry.set(code, []);
          (byCountry.get(code) as OrgSwitchRow[]).push(org);
        }
      });

      // The list is paged by name, so the root's row can sit on a page that
      // is not loaded yet. The user's own organization list (users/me) is
      // complete, so the root is pinned from it too -- under the same two
      // filters the server applies to the rows: the active product and the
      // search text (a root the user did not search for stays out).
      const needle = search.toLowerCase();
      profileRoots.forEach((org) => {
        if (pinnedRows.has(org.id)) return;
        if (activeProduct && !org.active_products.includes(activeProduct))
          return;
        if (!org.name.toLowerCase().includes(needle)) return;
        pinnedRows.set(org.id, org);
      });

      const labelForCode = (code: string) =>
        `${countryNameByCode[code] || code} (${code})`;

      const sections = Array.from(byCountry.entries())
        .map(([code, rows]) => ({ code, label: labelForCode(code), rows }))
        .sort((a, b) => a.label.localeCompare(b.label));

      return {
        pinned: Array.from(pinnedRows.values()).sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
        ungrouped: ungroupedRows,
        countrySections: sections,
        multiCountry: multiCountryRows,
      };
    }, [visibleOrgs, countryNameByCode, organizations, activeProduct, search]);

  // EC-3: block on both the org page and the country names so headers
  // never render with a bare code first and relabel a moment later -- one
  // fixed-height skeleton, no layout jump.
  const isInitialLoading = open && (!orgsQuery.data || isCountriesLoading);
  const showEmptyState =
    !isInitialLoading && pinned.length === 0 && visibleOrgs.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <IconButton
        sx={{
          position: 'absolute',
          right: '10px',
          top: '10px',
        }}
        onClick={onClose}
      >
        <CloseIcon sx={{ fontSize: '24px' }} />
      </IconButton>
      <Typography
        sx={{
          p: '20px',
          pb: '0px',
          fontSize: '24px',
          fontWeight: '700',
        }}
      >
        {t('topBar.organizations')}
      </Typography>

      <DialogContent sx={{ minHeight: '300px', minWidth: '600px' }}>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          size="small"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder={t('topBar.organizationsDialog.searchPlaceholder')}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: '#757575', mr: '4px' }} />,
          }}
          sx={{ mb: '16px' }}
        />

        {isInitialLoading && <PageLoading />}

        {!isInitialLoading && (
          <>
            {pinned.map((org) => (
              <OrganizationRow
                key={org.id}
                organization={org}
                depth={0}
                selected={organizationId === org.id}
                onSelect={() => navigateToOrganization(org.id)}
              />
            ))}

            {ungrouped.map((org) => (
              <OrganizationRow
                key={org.id}
                organization={org}
                depth={0}
                selected={organizationId === org.id}
                onSelect={() => navigateToOrganization(org.id)}
              />
            ))}

            {countrySections.map((section) => (
              <React.Fragment key={section.code}>
                <GroupHeader label={section.label} />
                {section.rows.map((org) => (
                  <OrganizationRow
                    key={org.id}
                    organization={org}
                    depth={1}
                    selected={organizationId === org.id}
                    onSelect={() => navigateToOrganization(org.id)}
                  />
                ))}
              </React.Fragment>
            ))}

            {multiCountry.length > 0 && (
              <>
                <GroupHeader
                  label={t('topBar.organizationsDialog.multipleCountries')}
                />
                {multiCountry.map((org) => (
                  <OrganizationRow
                    key={org.id}
                    organization={org}
                    depth={1}
                    selected={organizationId === org.id}
                    onSelect={() => navigateToOrganization(org.id)}
                  />
                ))}
              </>
            )}

            {showEmptyState && (
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ p: '20px', textAlign: 'center' }}
              >
                {t('topBar.organizationsDialog.noResults')}
              </Typography>
            )}

            {orgsQuery.hasNextPage && (
              <Button
                fullWidth
                onClick={() => orgsQuery.fetchNextPage()}
                disabled={orgsQuery.isFetchingNextPage}
              >
                {t('topBar.organizationsDialog.showMore')}
              </Button>
            )}
          </>
        )}
        <Divider />
      </DialogContent>
    </Dialog>
  );
}
